import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import SafeHtml from "./SafeHtml";
import { Sparkle, X, PaperPlaneRight, CircleNotch, Robot, FilePlus } from "@phosphor-icons/react";

function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`(.+?)`/g, "<code>$1</code>");
  const lines = (md || "").split("\n");
  const out = []; let list = null;
  const close = () => { if (list) { out.push(`</${list}>`); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    let m;
    if ((m = line.match(/^#{1,6}\s+(.*)/))) { close(); out.push(`<h4>${inline(m[1])}</h4>`); }
    else if ((m = line.match(/^\s*[-*]\s+(.*)/))) { if (list !== "ul") { close(); list = "ul"; out.push("<ul>"); } out.push(`<li>${inline(m[1])}</li>`); }
    else if ((m = line.match(/^\s*\d+[.)]\s+(.*)/))) { if (list !== "ol") { close(); list = "ol"; out.push("<ol>"); } out.push(`<li>${inline(m[1])}</li>`); }
    else if (line.trim() === "") { close(); }
    else { close(); out.push(`<p>${inline(line)}</p>`); }
  }
  close();
  return out.join("");
}

const SUGGESTIONS = [
  "How many active Aroflo jobs are there?",
  "Top 5 clients by revenue",
  "Which assets have condition reports outstanding?",
  "Summarise our technician workload",
];

export default function AssistantWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingIdx, setSavingIdx] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, history });
      setMessages((p) => [...p, { role: "assistant", content: data.answer || "", q: msg }]);
    } catch (e) {
      setMessages((p) => [...p, { role: "assistant", content: e.response?.data?.detail || "Sorry, something went wrong. Please try again.", error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const saveAsReport = async (m, idx) => {
    if (savingIdx !== null) return;
    setSavingIdx(idx);
    try {
      const { data } = await api.post("/reports/from-assistant", { question: m.q || "AI report", answer: m.content });
      toast.success("Report created", { action: { label: "Open", onClick: () => { setOpen(false); navigate(`/reports/${data.id}`); } } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the report. Please try again.");
    } finally {
      setSavingIdx(null);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        data-testid="assistant-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-[#7C3AED] lg:bottom-auto lg:top-4 lg:right-6"
      >
        <Sparkle size={16} weight="fill" className="text-[#A78BFA]" /> Ask AI
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="assistant-panel">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={() => setOpen(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#7C3AED]/10"><Robot size={18} weight="bold" className="text-[#7C3AED]" /></div>
                <div>
                  <p className="font-heading text-base font-black tracking-tight text-[#0A0A0A]">Asset Hub Assistant</p>
                  <p className="text-[11px] text-neutral-500">Answers from your imported data</p>
                </div>
              </div>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-neutral-100"><X size={18} weight="bold" /></button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-500">Hi! Ask me anything about your clients, assets, jobs, timesheets or condition reports.</p>
                  <div className="space-y-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                        className="block w-full rounded-sm border border-neutral-200 px-3 py-2 text-left text-sm text-neutral-700 transition-all duration-200 hover:border-[#7C3AED] hover:text-[#7C3AED]">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`} data-testid={`assistant-msg-${m.role}`}>
                  <div className={`max-w-[88%] rounded-sm px-3 py-2 text-sm ${
                    m.role === "user" ? "bg-[#0055FF] text-white"
                      : m.error ? "bg-[#DC2626]/10 text-[#DC2626]" : "bg-neutral-100 text-neutral-800"}`}>
                    {m.role === "assistant" && !m.error
                      ? <SafeHtml html={mdToHtml(m.content)} className="prose-asset" />
                      : m.content}
                  </div>
                  {m.role === "assistant" && !m.error && (
                    <button data-testid="assistant-save-report" onClick={() => saveAsReport(m, i)} disabled={savingIdx !== null}
                      className="mt-1.5 inline-flex items-center gap-1.5 rounded-sm border border-neutral-200 px-2.5 py-1 text-xs font-semibold text-[#7C3AED] transition-colors hover:bg-[#7C3AED]/5 disabled:opacity-50">
                      {savingIdx === i ? <CircleNotch size={13} className="animate-spin" /> : <FilePlus size={13} weight="bold" />} Save as report
                    </button>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="rounded-sm bg-neutral-100 px-3 py-2"><CircleNotch size={16} className="animate-spin text-[#7C3AED]" /></div></div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-neutral-200 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder="Ask about your data…"
                  className="max-h-28 flex-1 resize-none rounded-sm border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-[#7C3AED]"
                />
                <button data-testid="assistant-send" onClick={() => send()} disabled={loading || !input.trim()}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-[#7C3AED] text-white transition-all duration-200 hover:bg-[#6B21C8] disabled:opacity-50">
                  <PaperPlaneRight size={16} weight="bold" />
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-neutral-400">AI-generated — verify important figures against the source pages.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
